import {
  css,
  FilledInput as MuiFilledInput,
  filledInputClasses,
  inputAdornmentClasses,
  inputBaseClasses,
  inputClasses,
  styled,
} from '@mui/material';

import { textInputEndAdornmentIconClasses } from '../TextInput';
import { textInputGhostRootClasses } from './theme';

import type { Theme } from '../../types';
import type { TextInputGhostOwnerState, TextInputGhostProps } from './types';

// theme.ts의 MuiFilledInput styleOverrides 및 variants를 인라인으로 이전
export const StyledTextInputGhost = styled(
  MuiFilledInput,
  {},
)<{ ownerState: TextInputGhostOwnerState }>(({ ownerState, theme }) => {
  const { size, loading, success, multiline, error } = ownerState;

  return css`
    /* 기본 root 스타일 */
    ${getRootStyles(theme)}

    /* multiline 스타일 */
    ${multiline && getMultilineStyles(ownerState)}

    /* input 슬롯 스타일 */
    & .${filledInputClasses.input} {
      ${getInputStyles()}
    }

    /* error 스타일 */
    ${error && getErrorStyles(theme)}

    /* size별 스타일 */
    ${getSizeStyles(size, theme)}

    /* success 스타일 */
    ${success && getSuccessStyles(theme)}

    /* loading 스타일 */
    ${loading && getLoadingStyles(theme)}
  `;
});

// 스타일 헬퍼 함수들

const getRootStyles = (theme: Theme) => css`
  align-items: center;
  font-weight: 400;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: ${theme.color['text/default']};
  background-color: ${theme.color['surface/default']};
  padding-left: 8px;
  padding-right: 8px;
  border-radius: 1px;
  transition: none;

  &::before {
    border-bottom-color: transparent;
    transition: none;
  }

  &::after {
    border-bottom-width: 1px;
    transition: none;
  }

  & input,
  & textarea {
    &::placeholder {
      color: ${theme.color['text/weakest/default']};
      opacity: 1;
    }
  }

  /* hover */
  &:hover:not(.${inputBaseClasses.disabled}, .${inputBaseClasses.error}) {
    background-color: ${theme.color['surface/hover']};

    &:not(.${inputBaseClasses.focused}):before,
    &:not(.${inputBaseClasses.focused}):after {
      border-bottom-color: transparent;
    }
  }

  /* focus */
  &.${inputBaseClasses.focused} {
    background-color: ${theme.color['surface/default']};

    &::before,
    &::after {
      border-bottom-color: ${theme.color['focusRing/default']};
    }
  }

  /* disabled */
  &.${inputBaseClasses.disabled} {
    background-color: ${theme.color['surface/neutral/weakest/disabled']};
    pointer-events: none;

    &:before {
      border-bottom-style: solid;
    }

    & input,
    & textarea {
      &::placeholder {
        -webkit-text-fill-color: ${theme.color['text/disabled']};
      }
    }

    & > .${inputAdornmentClasses.root} {
      opacity: 0.3;
    }

    /* loading / error / success일 때 adornment는 선명하게 */
    &.${textInputGhostRootClasses.loading}
      > .${inputAdornmentClasses.root},
      &.${inputBaseClasses.error}
      > .${inputAdornmentClasses.root},
      &.${textInputGhostRootClasses.success}
      > .${inputAdornmentClasses.root} {
      opacity: 1;
    }
  }

  /* endAdornment - loading */
  & .${textInputEndAdornmentIconClasses.loadingIcon} {
    display: inline-block;
    position: relative;
  }

  & .${textInputEndAdornmentIconClasses.clearIcon} {
    color: ${theme.color['icon/strong/default']};
    cursor: pointer;

    &:hover {
      color: ${theme.color['icon/strong/hover']};
    }
  }
`;

const getMultilineStyles = (ownerState: TextInputGhostOwnerState) => css`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: fit-content;

  ${ownerState.resizable && ownerState.multiline && !ownerState.readOnly && !ownerState.disabled
    ? css`
        resize: both;
        min-height: 68px;
        max-height: 568px;
        min-width: 72px;
        max-width: min(960px, 100%);
        overflow-x: hidden;
      `
    : ''}

  & > .${inputClasses.inputMultiline} {
    overflow: auto !important;
  }

  & > .${inputAdornmentClasses.root}:has(*) {
    width: 100%;
    justify-content: flex-end;
    margin-left: 0;
    margin-top: auto;
    align-self: flex-end;
    flex-shrink: 0;
    height: fit-content;
  }
`;

const getInputStyles = () => css`
  padding: 0 4px;
  height: 20px;

  &.${textInputGhostRootClasses.overflow} {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const getErrorStyles = (theme: Theme) => css`
  &:hover:not(.${filledInputClasses.disabled}, .${textInputGhostRootClasses.loading}) {
    background-color: ${theme.color['surface/danger/weakest/default']};
  }

  &:not(.${textInputGhostRootClasses.loading}) {
    &:not(.${inputBaseClasses.focused}):before,
    &:not(.${inputBaseClasses.focused}):after {
      border-bottom-color: ${theme.color['border/danger/default']};
    }
  }

  &.${inputBaseClasses.focused} {
    background-color: ${theme.color['surface/default']};

    &::before,
    &::after {
      border-bottom-color: ${theme.color['focusRing/default']};
    }

    &:hover {
      background-color: ${theme.color['surface/danger/weakest/default']};
    }
  }

  & .${textInputEndAdornmentIconClasses.errorIcon} {
    color: ${theme.color['surface/danger/default']};
  }

  &.${inputBaseClasses.disabled} .${textInputEndAdornmentIconClasses.errorIcon} {
    color: ${theme.color['icon/danger/disabled']};
  }

  &.${inputBaseClasses.disabled}:not(.${textInputGhostRootClasses.loading}) {
    &:not(.${inputBaseClasses.focused}):before,
    &:not(.${inputBaseClasses.focused}):after {
      border-bottom-color: ${theme.color['icon/danger/disabled']};
    }
  }
`;

const getSizeStyles = (size: TextInputGhostProps['size'], theme: Theme) => {
  const sizeStylesMap = {
    sm: css`
      /* root */
      &.${filledInputClasses.root} {
        ${theme.typography['label/regular/sm']};
      }

      /* input */
      &.${filledInputClasses.root}:not(.${filledInputClasses.multiline}) {
        padding-top: 4px;
        padding-bottom: 4px;
      }

      /* multiline */
      &.${filledInputClasses.root}:is(.${filledInputClasses.multiline}) {
        padding-top: 8px;
        padding-bottom: 8px;
      }

      /* endAdornment */
      & > .${inputAdornmentClasses.root} {
        font-size: 11px;
        line-height: 16.5px;
        color: ${theme.color['icon/strong/hover']};

        & > *:not(.notranslate) + * {
          margin-left: 6px;
        }

        & svg {
          width: 16px;
          height: 16px;
          font-size: 16px;
        }

        & .${textInputEndAdornmentIconClasses.loadingIcon} {
          width: 16px;
          height: 16px;
          font-size: 16px;
        }
      }
    `,
    md: css`
      /* root */
      &.${filledInputClasses.root} {
        ${theme.typography['label/regular/md']};
      }

      /* input */
      &.${filledInputClasses.root}:not(.${filledInputClasses.multiline}) {
        padding-top: 6px;
        padding-bottom: 6px;
      }

      /* multiline */
      &.${filledInputClasses.root}:is(.${filledInputClasses.multiline}) {
        padding-top: 8px;
        padding-bottom: 8px;
      }

      /* endAdornment */
      & > .${inputAdornmentClasses.root} {
        font-size: 12px;
        line-height: 18px;
        color: ${theme.color['icon/strong/hover']};

        & > *:not(.notranslate) + * {
          margin-left: 6px;
        }

        & svg {
          width: 16px;
          height: 16px;
          font-size: 16px;
        }
      }
    `,
    lg: css`
      /* root */
      &.${filledInputClasses.root} {
        ${theme.typography['label/regular/md']};
      }

      /* input */
      &.${filledInputClasses.root}:not(.${filledInputClasses.multiline}) {
        padding-top: 10px;
        padding-bottom: 10px;
      }

      /* multiline */
      &.${filledInputClasses.root}:is(.${filledInputClasses.multiline}) {
        padding-top: 10px;
        padding-bottom: 10px;
      }

      /* endAdornment */
      & > .${inputAdornmentClasses.root} {
        font-size: 14px;
        line-height: 21px;
        color: ${theme.color['icon/strong/hover']};

        & > *:not(.notranslate) + * {
          margin-left: 8px;
        }

        & svg {
          width: 20px;
          height: 20px;
          font-size: 20px;
        }
      }
    `,
  };

  return sizeStylesMap[size || 'sm'] || css``;
};

const getSuccessStyles = (theme: Theme) => css`
  &:hover:not(
      .${filledInputClasses.disabled},
        .${inputBaseClasses.error},
        .${textInputGhostRootClasses.loading}
    ) {
    background-color: ${theme.color['surface/success/weakest/default']};

    &:not(.${inputBaseClasses.focused}):before,
    &:not(.${inputBaseClasses.focused}):after {
      border-bottom-color: ${theme.color['border/success/default']};
    }
  }

  &::before,
  &::after {
    border-bottom-color: ${theme.color['border/success/default']};
  }

  /* endAdornment */
  & .${textInputEndAdornmentIconClasses.successIcon} {
    color: ${theme.color['icon/success/default']};
  }

  &.${inputBaseClasses.disabled} .${textInputEndAdornmentIconClasses.successIcon} {
    color: ${theme.color['icon/success/disabled']};
  }

  &.${inputBaseClasses.disabled} {
    &::before,
    &::after {
      border-bottom-color: ${theme.color['icon/success/disabled']};
    }
  }
`;

const getLoadingStyles = (theme: Theme) => css`
  &.${textInputGhostRootClasses.loading} {
    &::before,
    &::after {
      border-bottom-color: transparent;
      transition: none;
    }

    &:not(.${filledInputClasses.disabled}) {
      background-color: ${theme.color['surface/default']};
    }
  }

  &.${textInputGhostRootClasses.loading}:hover:not(.${filledInputClasses.disabled}) {
    background-color: ${theme.color['surface/hover']};

    &:not(.${inputBaseClasses.focused}):before,
    &:not(.${inputBaseClasses.focused}):after {
      border-bottom-color: transparent;
    }
  }

  &.${textInputGhostRootClasses.loading}.${inputBaseClasses.focused} {
    background-color: ${theme.color['surface/default']};

    &::before,
    &::after {
      border-bottom-color: ${theme.color['focusRing/default']};
    }
  }
`;
